import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/CustomerApiNew';
import { compressWorkPhoto } from '../utils/compressWorkPhoto';
import './CreateTenderModal.css';

const API_BASE_URL = '/api/v1';
const MAX_PHOTOS = 5;

const isMobileDevice = () => {
	if (typeof window === 'undefined') return false;
	return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
		|| window.matchMedia('(max-width: 768px)').matches;
};

const CreateTenderModal = ({ onClose, onSuccess, loaderData }) => {
	const navigate = useNavigate();
	const cameraInputRef = useRef(null);
	const galleryInputRef = useRef(null);
	const fileInputRef = useRef(null);
	const filesInputRef = useRef(null);
	const [form, setForm] = useState({
		serviceType: '',
		serviceSubType: '',
		city: '',
		area: '',
		description: '',
		totalAreaSqft: '',
		numberOfFloors: '',
		propertyType: '',
		totalGuest: '',
		bookingDate: '',
		startDate: '',
		endDate: ''
	});
	const [photos, setPhotos] = useState([]);
	const [message, setMessage] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [requestingCity, setRequestingCity] = useState(false);
	const [cityCoverage, setCityCoverage] = useState({ cities: [], allIndia: false });
	const [loadingCities, setLoadingCities] = useState(false);
	const [showSourcePicker, setShowSourcePicker] = useState(false);

	const buildingServices = loaderData.buildingServices || [];
	const eventServices = loaderData.eventServices || [];
	const professionalServices = loaderData.professionalServices || [];
	const subTypeForBanquet = loaderData.subTypeForBanquet || [];


	const photosRef = useRef(photos);
	photosRef.current = photos;


	const TENDER_SERVICE_TYPES = [
		...buildingServices,
		...eventServices,
		...professionalServices];

	useEffect(() => () => {
		photosRef.current.forEach(p => URL.revokeObjectURL(p.preview));
	}, []);

	const updateField = (field, value) => {
		if (field === 'serviceType') {
			setForm(prev => ({
				...prev,
				serviceType: value,
				serviceSubType: '',
				propertyType: '',
				city: ''
			}));
			setMessage('');
			return;
		}
		setForm(prev => ({ ...prev, [field]: value }));
	};

	useEffect(() => {
		let cancelled = false;

		if (!form.serviceType) {
			setCityCoverage({ cities: [], allIndia: false });
			setLoadingCities(false);
			return () => {
				cancelled = true;
			};
		}

		setLoadingCities(true);
		setCityCoverage({ cities: [], allIndia: false });

		api.get(`${API_BASE_URL}/tenders/supported-cities`, {
			params: { serviceType: form.serviceType }
		})
			.then(res => {
				if (!cancelled) {
					setCityCoverage({
						cities: Array.isArray(res.data?.cities) ? res.data.cities : [],
						allIndia: Boolean(res.data?.allIndia)
					});
				}
			})
			.catch(err => {
				console.error('Failed to load supported cities', err);
				if (!cancelled) {
					setMessage('Unable to check service availability. Please try again.');
				}
			})
			.finally(() => {
				if (!cancelled) setLoadingCities(false);
			});

		return () => {
			cancelled = true;
		};
	}, [form.serviceType]);

	const getSubTypeOptions = () => {
		if (!loaderData || !form.serviceType) return [];
		if (professionalServices.includes(form.serviceType)) {
			return loaderData.subTypeForLawyer || [];
		}
		if (eventServices.includes(form.serviceType)) {
			return loaderData.subTypeForEvent || [];
		}
		if (form.serviceType === 'Ambulance') {
			return (loaderData.serviceSubType || []).filter(s =>
				s.toLowerCase().includes('ambulance') || ['Mini Ambulance', 'ICU Ambulance'].includes(s)
			).length > 0
				? (loaderData.serviceSubType || []).filter(s =>
					s.toLowerCase().includes('ambulance') || s === 'Multi Specialization'
				)
				: ['Mini Ambulance', 'ICU Ambulance'];
		}
		if (form.serviceType === 'Civil Contractor') {
			return loaderData.subTypeForCivilContractor || [];
		}
		if (buildingServices.includes(form.serviceType)) {
			return loaderData.subTypeForBuildingContractor || [];
		}
		return [];
	};

	const needsSubType = () => {
		return professionalServices.includes(form.serviceType)
			|| eventServices.includes(form.serviceType)
			|| form.serviceType === 'Ambulance'
			|| form.serviceType === 'Civil Contractor';
	};

	const needsBuildingFields = () => buildingServices.includes(form.serviceType);

	const normalizeCity = value => value.trim().replace(/\s+/g, ' ').toUpperCase();
	const citySupported = Boolean(
		form.city.trim()
		&& (cityCoverage.allIndia
			|| cityCoverage.cities.some(city => normalizeCity(city) === normalizeCity(form.city)))
	);
	const cityUnsupported = Boolean(
		form.serviceType && form.city.trim() && !loadingCities && !citySupported
	);

	const handleAddPhotos = (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;

		const remaining = loaderData.maxPhotos - photos.length;
		if (remaining <= 0) {
			setMessage(`Maximum ${loaderData.maxPhotos} photos allowed.`);
			e.target.value = '';
			return;
		}

		const selected = files.slice(0, remaining).filter(f => f.type.startsWith('image/'));
		if (selected.length < files.length) {
			setMessage('Only image files are allowed. Extra or invalid files were skipped.');
		} else {
			setMessage('');
		}

		const next = selected.map(file => ({
			id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
			file,
			preview: URL.createObjectURL(file)
		}));
		setPhotos(prev => [...prev, ...next]);
		[cameraInputRef, galleryInputRef, fileInputRef, filesInputRef].forEach(ref => {
			if (ref.current) ref.current.value = '';
		});
	};

	const handleAddPhotoClick = () => {
		if (photos.length >= loaderData.maxPhotos) {
			setMessage(`Maximum ${loaderData.maxPhotos} photos allowed.`);
			return;
		}
		setMessage('');
		if (isMobileDevice()) {
			setShowSourcePicker(true);
		} else {
			fileInputRef.current?.click();
		}
	};

	const handleSourcePick = (source) => {
		setShowSourcePicker(false);
		if (source === 'camera') cameraInputRef.current?.click();
		else if (source === 'gallery') galleryInputRef.current?.click();
		else filesInputRef.current?.click();
	};

	const removePhoto = (id) => {
		setPhotos(prev => {
			const target = prev.find(p => p.id === id);
			if (target) URL.revokeObjectURL(target.preview);
			return prev.filter(p => p.id !== id);
		});
	};

	const handleCityWaitlist = async () => {
		if (!form.serviceType || !form.city.trim() || !cityUnsupported) return;

		setMessage('');
		setRequestingCity(true);
		try {
			await api.post(`${API_BASE_URL}/tenders/city-waitlist`, {
				serviceType: form.serviceType,
				city: form.city.trim()
			});
			setMessage(
				`Service request saved successfully! We will notify you when ${form.serviceType} providers become available in ${form.city.trim()}.`
			);
		} catch (err) {
			const errMsg = err.response?.data?.message || err.response?.data?.error
				|| 'Failed to save your city request';
			setMessage(typeof errMsg === 'string' ? errMsg : 'Failed to save your city request');
		} finally {
			setRequestingCity(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setMessage('');

		// Get today's date formatted as YYYY-MM-DD for clean comparison
		const todayStr = new Date().toISOString().split('T')[0];

		// 1. Check for blank fields
		if (!form.startDate || !form.endDate) {
			setMessage('Start date and End date cannot be blank.');
			return;
		}

		if (eventServices.includes(form.serviceType) && !form.bookingDate) {
			setMessage('Booking date cannot be blank.');
			return;
		}

		// 2. Validate Past Dates
		if (form.startDate < todayStr) {
			setMessage('Tender Start Date cannot be a past date.');
			return;
		}

		if (form.endDate < todayStr) {
			setMessage('Tender End Date cannot be a past date.');
			return;
		}

		if (eventServices.includes(form.serviceType) && form.bookingDate < todayStr) {
			setMessage('Booking Date cannot be a past date.');
			return;
		}

		// 3. Validate End Date vs Start Date
		if (form.endDate < form.startDate) {
			setMessage('Tender End Date cannot be before the Start Date.');
			return;
		}

		if (form.bookingDate && form.startDate > form.bookingDate) {
			setMessage('Tender Start Date cannot be after Booking Date');
			return;
		}

		if (form.bookingDate && form.endDate > form.bookingDate) {
			setMessage('Tender should End before Booking Date');
			return;
		}


		if (loadingCities) {
			setMessage('Please wait while we check service availability.');
			return;
		}
		if (!citySupported) {
			setMessage(
				`This service is not available in ${form.city.trim()} yet. Use "Request service in this city" to join the waitlist.`
			);
			return;
		}

		setSubmitting(true);

		const payload = {
			serviceType: form.serviceType,
			city: form.city.trim().toUpperCase(),
			area: form.area?.trim() || null,
			description: form.description?.trim() || null,
			startDate: form.startDate,
			endDate: form.endDate,
			bookingDate: form.bookingDate || null,
			totalGuest: form.totalGuest || null
		};

		if (needsSubType()) {
			payload.serviceSubType = form.serviceSubType;
		}
		if (needsBuildingFields()) {
			payload.totalAreaSqft = parseFloat(form.totalAreaSqft);
			payload.numberOfFloors = parseInt(form.numberOfFloors, 10);
			payload.propertyType = form.propertyType;
			if (form.serviceType === 'Civil Contractor' && form.serviceSubType) {
				payload.serviceSubType = form.serviceSubType;
			}
		}

		try {
			const formData = new FormData();
			formData.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));

			for (const photo of photos) {
				const compressed = await compressWorkPhoto(photo.file);
				formData.append('photos', compressed, 'tender.webp');
			}

			await api.post(`${API_BASE_URL}/tenders`, formData);
			setMessage('Tender created successfully!');
			setTimeout(() => {
				onSuccess?.();
				onClose();
				navigate('/customer-dashboard');
			}, 800);
		} catch (err) {
			const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create tender';
			setMessage(typeof errMsg === 'string' ? errMsg : 'Failed to create tender');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="tender-modal-overlay" onClick={onClose}>
			<div className="tender-modal" onClick={e => e.stopPropagation()}>
				<div className="tender-modal-header">
					<h2>Create Tender</h2>
					<button type="button" className="tender-modal-close" onClick={onClose}>&times;</button>
				</div>

				<form onSubmit={handleSubmit} className="tender-form">
					<div className="form-group">
						<label>Service Type *</label>
						<select value={form.serviceType} onChange={e => updateField('serviceType', e.target.value)} required>
							<option value="">Select Service Type</option>
							{TENDER_SERVICE_TYPES.map(s => (
								<option key={s} value={s}>{s}</option>
							))}
						</select>
					</div>

					{needsSubType() && (
						<div className="form-group">
							<label>
								{eventServices.includes(form.serviceType) ? 'Event Type' :
									form.serviceType === 'Ambulance' ? 'Ambulance Type' :
										form.serviceType === 'Civil Contractor' ? 'Work Type' : 'Specialization'} *
							</label>
							<select value={form.serviceSubType} onChange={e => updateField('serviceSubType', e.target.value)} required>
								<option value="">Select</option>
								{getSubTypeOptions().map(opt => (
									<option key={opt} value={opt}>{opt}</option>
								))}
							</select>
						</div>
					)}

					{eventServices.includes(form.serviceType) && (
						<div className="form-group">
							<label>
								Booking Date *
							</label>
							<input type="date" value={form.bookingDate} min={new Date().toISOString().split('T')[0]}
								onChange={e => updateField('bookingDate', e.target.value)} required />

						</div>
					)}

					{subTypeForBanquet.includes(form.serviceType) && (
						<div className="form-group">
							<label>
								Total Guest *
							</label>
							<input type="number" value={form.totalGuest}
								onChange={e => updateField('totalGuest', e.target.value)} required />

						</div>
					)}

					{needsBuildingFields() && (
						<>
							<div className="form-group">
								<label>Property Type *</label>
								<select value={form.propertyType} onChange={e => updateField('propertyType', e.target.value)} required>
									<option value="">Select Property Type</option>
									{(loaderData?.subTypeForBuildingContractor || []).map(opt => (
										<option key={opt} value={opt}>{opt}</option>
									))}
								</select>
							</div>
							<div className="form-row">
								<div className="form-group">
									<label>Total Area (SQFT) *</label>
									<input type="number" min="1" step="0.01" value={form.totalAreaSqft}
										onChange={e => updateField('totalAreaSqft', e.target.value)} required />
								</div>
								<div className="form-group">
									<label>No. of Floors *</label>
									<input type="number" min="1" value={form.numberOfFloors}
										onChange={e => updateField('numberOfFloors', e.target.value)} required />
								</div>
							</div>
						</>
					)}

					<div className="form-group">
						<label>City *</label>
						<input
							type="text"
							list="supported-tender-cities"
							value={form.city}
							onChange={e => {
								updateField('city', e.target.value);
								setMessage('');
							}}
							placeholder={form.serviceType ? 'Start typing your city' : 'Select a service first'}
							disabled={!form.serviceType || loadingCities}
							autoComplete="off"
							required
						/>
						<datalist id="supported-tender-cities">
							{cityCoverage.cities.map(city => (
								<option key={city} value={city} />
							))}
						</datalist>
						{loadingCities && <p className="city-availability checking">Checking available cities...</p>}
						{!loadingCities && citySupported && (
							<p className="city-availability available">Service is available in this city.</p>
						)}
						{cityUnsupported && (
							<div className="unsupported-city-box">
								<p>This service is not available in this city yet.</p>
								<button
									type="button"
									className="btn-city-waitlist"
									onClick={handleCityWaitlist}
									disabled={requestingCity}
								>
									{requestingCity ? 'Saving request...' : 'Request service in this city'}
								</button>
							</div>
						)}
					</div>

					<div className="form-group">
						<label>Area (optional)</label>
						<input
							type="text"
							value={form.area}
							onChange={e => updateField('area', e.target.value)}
							placeholder="e.g. Koramangala, MG Road, Sector 5"
						/>
					</div>

					<div className="form-group">
						<label>Work Description *</label>
						<textarea rows={4} value={form.description} onChange={e => updateField('description', e.target.value)}
							placeholder="Describe your work requirements in detail..." required />
					</div>

					<div className="form-group">
						<label>Reference Photos (optional, max {MAX_PHOTOS})</label>
						<p className="tender-photos-hint">
							Add site/work photos so providers can quote more accurately. Images are compressed before upload.
						</p>
						<div className="tender-photos-toolbar">
							<span>{photos.length} / {MAX_PHOTOS}</span>
							<button
								type="button"
								className="btn-add-photo"
								disabled={photos.length >= MAX_PHOTOS || submitting}
								onClick={handleAddPhotoClick}
							>
								+ Add Photos
							</button>
							<input
								ref={cameraInputRef}
								type="file"
								accept="image/*"
								capture="environment"
								hidden
								onChange={handleAddPhotos}
							/>
							<input
								ref={galleryInputRef}
								type="file"
								accept="image/*"
								multiple
								hidden
								onChange={handleAddPhotos}
							/>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								multiple
								hidden
								onChange={handleAddPhotos}
							/>
							<input
								ref={filesInputRef}
								type="file"
								accept="*/*"
								multiple
								hidden
								onChange={handleAddPhotos}
							/>
						</div>
						{photos.length > 0 && (
							<div className="tender-photos-grid">
								{photos.map(photo => (
									<div key={photo.id} className="tender-photo-item">
										<img src={photo.preview} alt="Reference" />
										<button
											type="button"
											className="tender-photo-remove"
											onClick={() => removePhoto(photo.id)}
											aria-label="Remove photo"
										>
											×
										</button>
									</div>
								))}
							</div>
						)}
					</div>

					<div className="form-row">
						<div className="form-group">
							<label>Tender Start Date *</label>
							<input type="date" value={form.startDate} min={new Date().toISOString().split('T')[0]} // Prevents picking past dates
								max={form.bookingDate} onChange={e => updateField('startDate', e.target.value)} required />
						</div>
						<div className="form-group">
							<label>Tender End Date *</label>
							<input type="date" value={form.endDate} min={form.startDate || new Date().toISOString().split('T')[0]}
								max={form.bookingDate} onChange={e => updateField('endDate', e.target.value)} required />
						</div>
					</div>

					{message && <p className={`tender-message ${message.includes('success') ? 'success' : 'error'}`}>{message}</p>}

					<div className="tender-form-actions">
						<button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
						<button
							type="submit"
							className="btn-primary"
							disabled={submitting || loadingCities || !citySupported}
						>
							{submitting ? 'Creating...' : 'Create Tender'}
						</button>
					</div>
				</form>
			</div>

			{showSourcePicker && (
				<div className="photo-source-overlay" onClick={() => setShowSourcePicker(false)}>
					<div className="photo-source-sheet" onClick={(e) => e.stopPropagation()}>
						<p className="photo-source-title">Add photo from</p>
						<button type="button" className="photo-source-option" onClick={() => handleSourcePick('camera')}>
							Camera
						</button>
						<button type="button" className="photo-source-option" onClick={() => handleSourcePick('gallery')}>
							Gallery
						</button>
						<button type="button" className="photo-source-option" onClick={() => handleSourcePick('file')}>
							Files
						</button>
						<button type="button" className="photo-source-cancel" onClick={() => setShowSourcePicker(false)}>
							Cancel
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default CreateTenderModal;
